import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const FamoraLogo = ({ size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 200 200">
    <defs>
      <linearGradient id="lb" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1A1040"/>
        <stop offset="100%" stopColor="#2D1B6B"/>
      </linearGradient>
    </defs>
    <rect width="200" height="200" rx="44" fill="url(#lb)"/>
    <circle cx="72" cy="62" r="18" fill="none" stroke="#7B9FE8" strokeWidth="6"/>
    <path d="M44 118 Q44 92 72 92 Q100 92 100 118" fill="none" stroke="#7B9FE8" strokeWidth="6" strokeLinecap="round"/>
    <circle cx="138" cy="54" r="16" fill="none" stroke="#F0B45A" strokeWidth="6"/>
    <path d="M113 110 Q113 86 138 86 Q163 86 163 110" fill="none" stroke="#F0B45A" strokeWidth="6" strokeLinecap="round"/>
    <circle cx="105" cy="70" r="14" fill="none" stroke="#9B8FE4" strokeWidth="6"/>
    <path d="M83 118 Q83 100 105 100 Q127 100 127 118" fill="none" stroke="#9B8FE4" strokeWidth="6" strokeLinecap="round"/>
    <path d="M105 158 L88 141 Q80 132 88 123 Q97 114 105 123 Q113 114 122 123 Q130 132 122 141 Z" fill="#F0B45A"/>
    <path d="M48 120 Q45 148 105 162 Q165 148 162 120" fill="none" stroke="#9B8FE4" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
  </svg>
);

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style={{flexShrink:0}}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{flexShrink:0}}>
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

export default function LoginPage() {
  const { login, loginWithToken } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm]       = useState({ email: '', password: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleToken = params.get('google_token');
    const googleError = params.get('google_error');
    if (googleToken) {
      loginWithToken(googleToken).then(() => navigate('/')).catch(() => setError('Google-innlogging feilet'));
    }
    if (googleError) setError(`Google-innlogging feilet: ${googleError}`);
  }, [loginWithToken, navigate]);

  const handle = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Innlogging feilet');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #1A0A4E 0%, #3D1B8E 35%, #1A1245 70%, #0D0B1E 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, position: 'relative', overflow: 'hidden',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      {/* Glødende bakgrunnsblobs */}
      <div style={{ position:'absolute', top:-120, right:-120, width:450, height:450, borderRadius:'50%', background:'rgba(124,111,247,0.28)', filter:'blur(90px)', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', bottom:-150, left:-100, width:400, height:400, borderRadius:'50%', background:'rgba(93,58,227,0.22)', filter:'blur(90px)', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', top:'40%', left:'-8%', width:220, height:220, borderRadius:'50%', background:'rgba(147,88,232,0.18)', filter:'blur(60px)', pointerEvents:'none' }}/>

      {/* Kortcontainer */}
      <div style={{
        width: '100%', maxWidth: 400,
        background: 'rgba(255,255,255,0.06)',
        backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)',
        borderRadius: 28,
        border: '1px solid rgba(255,255,255,0.13)',
        boxShadow: '0 28px 70px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
        overflow: 'hidden',
      }}>
        {/* Gradient-topp med logo og tittel */}
        <div style={{
          background: 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 50%, #5E4AE3 100%)',
          padding: '36px 32px 32px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:150, height:150, borderRadius:'50%', background:'rgba(255,255,255,0.08)', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', bottom:-50, left:-30, width:130, height:130, borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none' }}/>
          <div style={{ position:'relative', zIndex:1 }}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
              <FamoraLogo size={68} />
            </div>
            <h1 style={{ fontSize:30, fontWeight:800, color:'#fff', margin:'0 0 6px', letterSpacing:'-0.5px' }}>Famora</h1>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.72)', margin:0 }}>En app for hele familien 👨‍👩‍👧‍👦</p>
          </div>
        </div>

        {/* Skjema-innhold */}
        <div style={{ padding: '28px 32px 32px' }}>
          {error && (
            <div style={{ background:'rgba(239,68,68,0.15)', color:'#FCA5A5', padding:'10px 14px', borderRadius:12, fontSize:13, marginBottom:18, border:'1px solid rgba(239,68,68,0.3)' }}>
              {error}
            </div>
          )}

          <form onSubmit={handle} style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'.8px', marginBottom:2 }}>E-post</label>
            <input
              style={{ padding:'12px 14px', borderRadius:12, border:'1px solid rgba(255,255,255,0.13)', background:'rgba(255,255,255,0.08)', fontFamily:'inherit', fontSize:14, color:'#fff', outline:'none', marginBottom:14 }}
              type="email" autoComplete="email" value={form.email}
              onChange={e => setForm(f => ({...f, email: e.target.value}))} required
            />
            <label style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'.8px', marginBottom:2 }}>Passord</label>
            <input
              style={{ padding:'12px 14px', borderRadius:12, border:'1px solid rgba(255,255,255,0.13)', background:'rgba(255,255,255,0.08)', fontFamily:'inherit', fontSize:14, color:'#fff', outline:'none', marginBottom:6 }}
              type="password" autoComplete="current-password" value={form.password}
              onChange={e => setForm(f => ({...f, password: e.target.value}))} required
            />
            <button
              style={{ padding:'14px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#7C6FF7,#5B4FD4)', color:'#fff', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer', marginTop:10, boxShadow:'0 4px 20px rgba(124,111,247,0.4)', opacity: loading ? 0.7 : 1 }}
              type="submit" disabled={loading}>
              {loading ? 'Logger inn…' : 'Logg inn'}
            </button>
          </form>

          <div style={{ display:'flex', alignItems:'center', gap:12, margin:'20px 0 16px', color:'rgba(255,255,255,0.18)', fontSize:12 }}>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.1)' }}/>
            <span style={{ fontWeight:600, letterSpacing:'.5px' }}>ELLER</span>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.1)' }}/>
          </div>

          <a href="/api/auth/facebook" style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'12px', borderRadius:13, border:'none', background:'#1877F2', color:'#fff', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer', textDecoration:'none', width:'100%', boxSizing:'border-box', marginBottom:10 }}>
            <FacebookIcon /> Fortsett med Facebook
          </a>
          <a href="/api/google/auth?action=login" style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'12px', borderRadius:13, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.07)', color:'#fff', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer', textDecoration:'none', width:'100%', boxSizing:'border-box' }}>
            <GoogleIcon /> Fortsett med Google
          </a>

          <p style={{ textAlign:'center', fontSize:13, color:'rgba(255,255,255,0.38)', marginTop:22, marginBottom:0 }}>
            Ny familie? <Link to="/registrer" style={{ color:'#A78BFA', fontWeight:700, textDecoration:'none' }}>Opprett konto</Link>
          </p>

          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:16 }}>
            <a href="https://famora.no/personvern" style={{ fontSize:11, color:'rgba(255,255,255,0.22)', textDecoration:'none' }} rel="noreferrer" target="_blank">Personvern</a>
            <span style={{ color:'rgba(255,255,255,0.15)', fontSize:11 }}>·</span>
            <a href="https://famora.no/vilkar" style={{ fontSize:11, color:'rgba(255,255,255,0.22)', textDecoration:'none' }} rel="noreferrer" target="_blank">Vilkår</a>
          </div>
        </div>
      </div>
    </div>
  );
}
